import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Search, Filter, Calendar, Tag, ArrowLeft, ChevronRight, Share2, User, CheckCircle, AlertCircle } from "lucide-react";
import { useNavigationStore } from "@/stores/navigation";
import { BLOG_POSTS } from "@/data/blogPosts";
import { emailService } from "@/services/emailService";

// --- Mock Data ---
const POSTS = [
  {
    id: "referrals-unified-platform",
    title: "The Future of Referrals: Why Internal & External Referrals Need a Unified Platform",
    kicker: "ReferralCX",
    excerpt:
      "Fragmented referral systems increase leakage, delays, and denials. Here’s how a unified approach improves access, throughput, and outcomes.",
    subtitle:
      "Fragmented referral systems increase leakage, delays, and denials. Here’s how a unified approach improves access, throughput, and outcomes.",
    tags: ["ReferralCX"],
    author: "Dawniela Hightower",
    date: "2025-09-10",
    read: 6,
    sections: [
      {
        h2: "Why referrals break today",
        p: [
          "Most organizations juggle internal specialty referrals, external community referrals, and self-referrals across disconnected tools. This creates opaque handoffs, inconsistent triage, and delayed booking—ultimately driving leakage and denials.",
          "When referral requests arrive via fax, email, or portals with different intake fields, staff spend time normalizing data instead of helping patients. Leaders get volume without visibility and can’t forecast demand or capacity.",
        ],
        bullets: [
          "Duplicate data entry and swivel‑chair workflows",
          "Eligibility/authorization checks performed late (or not at all)",
          "Routing rules live in people’s heads instead of the platform",
          "No shared status signal across facilities and partners",
        ],
      },
      {
        h2: "A unified referral model",
        p: [
          "A modern platform should bring **all** referral types into a single pipeline with configurable steps. In Eceptionist’s ReferralCX, each referral moves through standardized states with role‑based tasks and SLAs—so every team sees the same source of truth.",
        ],
        bullets: [
          "Digital intake with templates and validation",
          "Automated triage and program/service matching",
          "Capacity‑aware scheduling and patient communications",
          "Complete audit trail and analytics",
        ],
      },
      {
        h2: "What this changes for ops",
        p: [
          "Teams shift from chasing paperwork to managing outcomes. With shared worklists and queue health, leaders can balance caseloads, reduce backlog, and accelerate time‑to‑book.",
        ],
        bullets: [
          "\u003cstrong\u003e30–50%\u003c/strong\u003e less manual rework via templates and rules",
          "\u003cstrong\u003e15–25%\u003c/strong\u003e faster time‑to‑scheduled thanks to guided triage",
          "Fewer denials by catching eligibility issues up front",
        ],
        note: "Benchmarks vary by program size and payer mix; your results may differ.",
      },
      {
        h2: "Design principles we follow",
        p: [
          "We build for real‑world complexity: multiple facilities, programs, and external partners. Our approach emphasizes governance without sacrificing speed.",
        ],
        bullets: [
          "\u003cstrong\u003eSingle pipeline, multiple views:\u003c/strong\u003e coordinators, schedulers, and leaders each see what they need—on the same data.",
          "\u003cstrong\u003eConfig over custom:\u003c/strong\u003e admin UI for forms, routing, SLAs, and notifications.",
          "\u003cstrong\u003eHuman‑in‑the‑loop AI:\u003c/strong\u003e suggestions for routing/urgency with transparent audit trails.",
        ],
      },
      {
        h2: "Getting started",
        p: [
          "Most teams pilot one or two services first—then scale. We recommend mapping current states, defining target SLAs, and capturing a minimum service catalog with intake rules.",
        ],
        checklist: [
          "Pick 2–3 high‑volume services",
          "Document intake fields and eligibility rules",
          "Define routing logic (site, provider, payer, language, risk)",
          "Stand up referral worklists and dashboards",
          "Train coordinators and schedulers on the new workflow",
        ],
      },
    ],
  },
  {
    id: "ai-in-cx",
    title: "AI in EceptionistCX: Beyond the Hype, Toward Practical Impact",
    kicker: "AICX",
    excerpt:
      "Where AI actually pays off: triage, routing, eligibility validation, and next‑best actions—not just chatbots.",
    subtitle:
      "Where AI actually pays off: capturing referrals, triage, routing, and resource utilization embedded into workflows.",
    tags: ["AI"],
    author: "Dirk Voorhees",
    date: "2025-09-09",
    read: 6,
    sections: [
      {
        h2: "Why AI in operations",
        p: [
          "Healthcare systems often look to technology to reduce friction, but the biggest efficiency gains come from streamlining referral capture, routing, triage, and task prioritization.",
          "For example, using AI to read faxed referrals and turn them into structured electronic referrals eliminates hours of manual data entry while reducing errors. Coordinators can start work with complete, discrete data from day one.",
        ],
        bullets: [
          "AI‑scored urgency for incoming referrals",
          "Automated capture of faxed referrals into electronic forms",
          "Suggested routing based on service catalogs and program rules",
          "Next‑best‑action prompts for coordinators",
        ],
      },
      {
        h2: "Our approach to AI in EceptionistCX",
        p: [
          "We focus on \u003cstrong\u003ehuman‑in‑the‑loop\u003c/strong\u003e models. The system makes recommendations—staff make the final calls. This keeps governance intact while reducing repetitive checks.",
          "AI also helps manage capacity by surfacing where bottlenecks are forming—beds, infusion chairs, clinic appointments—and proactively suggesting adjustments to scheduling or resource allocation.",
        ],
        bullets: [
          "Transparent logic with audit trails",
          "Configurable confidence thresholds",
          "Dashboards showing impact on wait times, resource utilization, and rework",
        ],
      },
      {
        h2: "What customers see in practice",
        p: [
          "Early adopters of AI in CX modules report reductions in both staff burden and patient delays. By capturing referrals electronically and surfacing next‑steps instantly, staff shift focus from data entry to patient engagement. Capacity management features ensure resources are aligned with demand.",
        ],
        bullets: [
          "\u003cstrong\u003e20–30%\u003c/strong\u003e fewer manual escalations and transcription errors",
          "\u003cstrong\u003e10–15%\u003c/strong\u003e faster referral cycle times",
          "Improved alignment of staff, beds, and appointment slots with demand",
        ],
      },
      {
        h2: "Design principles we follow",
        p: [
          "AI is only as useful as its adoption. That means we design lightweight UI hints instead of heavy, disruptive pop‑ups.",
        ],
        bullets: [
          "Prompts embedded directly in worklists",
          "Explainability: staff can see why AI made a recommendation",
          "Continuous learning with admin‑tunable rules",
        ],
      },
      {
        h2: "How to start with AI in CX",
        p: [
          "We recommend piloting AI on one or two narrow use cases—like referral fax capture or urgency scoring—before scaling across departments.",
        ],
        checklist: [
          "Identify 1–2 high‑volume workflows with delays or manual data entry",
          "Define measurable metrics (time to schedule, cycle times, resource utilization)",
          "Enable AI in CX prompts in coordinator worklists",
          "Review results after 60 days and expand scope",
        ],
      },
    ],
  },
  {
    id: "cellcx-from-lab-to-bedside",
    title: "From Lab to Bedside: Orchestrating Complex Cell Therapy Workflows with CellCX",
    kicker: "CellCX",
    excerpt:
      "CAR‑T requires precise, multi-party coordination. CellCX helps harmonize eligibility, apheresis, manufacturing, logistics, and infusion scheduling.",
    subtitle:
      "How purpose‑built workflow tools accelerate CAR‑T and other advanced therapies from collection to infusion.",
    tags: ["CellCX"],
    author: "Dawniela Hightower",
    date: "2025-09-07",
    read: 8,
    sections: [
      { h2: "Why cell therapy needs purpose‑built workflows", p: [
        "Cell therapy is unlike traditional infusions. Each patient is their own batch, with handoffs across apheresis, manufacturing, labs, logistics, and clinical teams.",
        "Generic scheduling tools break down when you need milestone‑based orchestration across multiple teams. That’s where CellCX comes in.",
      ], bullets: [
        "Manage patient journey from referral to infusion",
        "Coordinate labs, couriers, and manufacturing slots",
        "Surface bottlenecks early to avoid delays",
      ]},
      { h2: "How CellCX works", p: [
        "CellCX provides a configurable backbone for advanced therapies. Each case follows a state machine with tasks, SLAs, and notifications.",
        "Teams see the same single source of truth while still having role‑based dashboards for coordinators, lab staff, and investigators.",
      ], bullets: [
        "Digital intake with therapy‑specific templates",
        "Milestone tracking (collection, manufacturing, release, infusion)",
        "Alerts for deviations or delays",
        "Analytics on turnaround times and slot utilization",
      ]},
      { h2: "What this changes for programs", p: [
        "Programs using CellCX report fewer missed milestones, smoother cross‑team handoffs, and more predictable infusion dates.",
      ], bullets: [
        "\u003cstrong\u003e20–40%\u003c/strong\u003e fewer last‑minute reschedules",
        "Better utilization of scarce manufacturing slots",
        "Reduced coordinator burnout from manual spreadsheets",
      ]},
      { h2: "Design principles we follow", p: [
        "Cell therapy demands rigor without sacrificing speed. We designed CellCX with both compliance and usability in mind.",
      ], bullets: [
        "\u003cstrong\u003eTransparency first:\u003c/strong\u003e status visible across sites and vendors.",
        "\u003cstrong\u003eNo black boxes:\u003c/strong\u003e progress is always visible and auditable.",
        "\u003cstrong\u003eConfig over custom:\u003c/strong\u003e adapt templates and rules without coding.",
      ]},
      { h2: "Getting started with CellCX", p: [
        "Most centers start with CAR‑T and expand to other therapies. Success depends on mapping your milestones, defining SLA expectations, and engaging both clinical and lab teams early.",
      ], checklist: [
        "Define therapy types and milestone states",
        "Configure intake forms and workflow rules",
        "Set alerts for lab/manufacturing turnaround times",
        "Pilot with 5–10 patients before full rollout",
      ]},
    ],
  },
  {
    id: "probationcx-digital-referrals",
    title: "Modernizing Probation: How ProbationCX Digitizes Referrals and Case Management",
    kicker: "ProbationCX",
    excerpt:
      "Paper-based referrals and manual status checks slow outcomes. See how ProbationCX improves visibility, compliance, and caseload balance.",
    subtitle:
      "Moving from paper and spreadsheets to automated referrals, digital workflows, and real‑time reporting.",
    tags: ["ProbationCX"],
    author: "Dawniela Hightower",
    date: "2025-09-06",
    read: 7,
    sections: [
      { h2: "Why probation services need modernization", p: [
        "Probation departments handle thousands of referrals every year—from courts, treatment providers, and community programs.",
        "Paper‑based intake and siloed spreadsheets create delays, errors, and compliance risks. Staff spend more time chasing paperwork than focusing on client success.",
      ], bullets: [
        "Referrals arrive by paper, fax, or email with no consistent format",
        "Manual data entry increases errors and workload",
        "Little visibility for supervisors or stakeholders",
      ]},
      { h2: "How ProbationCX works", p: [
        "ProbationCX brings all referrals and case data into a unified digital platform. Every referral moves through configurable steps with tasks, alerts, and SLAs.",
        "Supervisors gain real‑time dashboards, while officers access clear worklists and communication tools.",
      ], bullets: [
        "Digital referral intake with validation rules",
        "Automated routing to officers or programs",
        "Shared dashboards with status and outcomes",
        "Alerts for overdue tasks or missed milestones",
      ]},
      { h2: "What this changes for departments", p: [
        "Agencies using ProbationCX reduce delays, improve compliance, and free up staff capacity.",
      ], bullets: [
        "\u003cstrong\u003e30–40%\u003c/strong\u003e reduction in manual data entry",
        "Improved visibility into caseload distribution",
        "Better collaboration with courts and providers",
      ]},
      { h2: "Design principles we follow", p: [
        "Probation systems must balance accountability, compliance, and rehabilitation. ProbationCX was designed with these values in mind.",
      ], bullets: [
        "\u003cstrong\u003eConfigurable forms and workflows:\u003c/strong\u003e adapt to each jurisdiction’s requirements.",
        "\u003cstrong\u003eTransparency first:\u003c/strong\u003e officers, supervisors, and courts see the same data.",
        "\u003cstrong\u003eScalable architecture:\u003c/strong\u003e support for small counties and large state systems.",
      ]},
      { h2: "Getting started with ProbationCX", p: [
        "Most departments begin by digitizing their referral intake process, then expand into broader case management.",
      ], checklist: [
        "Map current referral sources and formats",
        "Define routing logic (officer assignment, program type)",
        "Configure dashboards for supervisors",
        "Pilot with a small caseload before scaling",
      ]},
    ],
  },
  {
    id: "vaxmanagercx-public-health-readiness",
    title: "Public Health Readiness: What COVID Taught Us About Vaccine & Clinic Management",
    kicker: "VaxManagerCX",
    excerpt:
      "VaxManagerCX supports mass clinics, employer programs, and ongoing immunization operations with inventory, eligibility, and scheduling tools.",
    subtitle:
      "How VaxManagerCX supports mass clinics, employer programs, and ongoing immunization operations.",
    tags: ["VaxManagerCX"],
    author: "Trey Havlick",
    date: "2025-09-03",
    read: 5,
    sections: [
      { h2: "Why vaccine management needs modernization", p: [
        "COVID‑19 highlighted how unprepared many systems were for mass vaccination. Scheduling backlogs, long lines, and data silos slowed public health response.",
        "Ongoing readiness means treating vaccine and clinic management as a core capability, not a one‑time event.",
      ], bullets: [
        "Long wait times due to manual scheduling",
        "Limited reporting for state and federal agencies",
        "Difficulty handling large wait lists or eligibility groups",
      ]},
      { h2: "How VaxManagerCX works", p: [
        "VaxManagerCX provides a unified platform to manage clinics, patient scheduling, and communications.",
        "It supports high‑volume mass vaccination events as well as routine employer and community programs.",
      ], bullets: [
        "Online scheduling with eligibility checks",
        "Wait list management when demand exceeds available appointments",
        "Digital consent forms and pre‑visit questionnaires",
        "Real‑time dashboards for doses administered",
        "Integration with immunization registries",
      ]},
      { h2: "What this changes for programs", p: [
        "Agencies using VaxManagerCX report smoother clinics, higher throughput, and improved reporting accuracy.",
      ], bullets: [
        "\u003cstrong\u003e25–35%\u003c/strong\u003e faster appointment throughput",
        "Improved compliance with public health reporting requirements",
        "Better patient experience through streamlined forms, consents, and wait list handling",
      ]},
      { h2: "Design principles we follow", p: [
        "Public health systems must be resilient, adaptable, and transparent. VaxManagerCX was built with these principles in mind.",
      ], bullets: [
        "\u003cstrong\u003eScalable architecture:\u003c/strong\u003e support for both mass clinics and small programs.",
        "\u003cstrong\u003eConfigurable workflows:\u003c/strong\u003e adapt eligibility, reminders, and reporting rules.",
        "\u003cstrong\u003eTransparent dashboards:\u003c/strong\u003e real‑time data for staff and leadership.",
      ]},
      { h2: "Getting started with VaxManagerCX", p: [
        "Most organizations begin with scheduling and patient intake, then expand to communications and reporting.",
      ], checklist: [
        "Set up scheduling rules for clinics and programs",
        "Integrate with state immunization registry",
        "Configure digital consent forms and questionnaires",
        "Pilot with one employer or community program before scaling",
      ]},
    ],
  },
];

function formatDate(iso: string) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// LIGHT BLUE LOOK & FEEL HELPERS
const liteBlue = {
  bgBand: "bg-[#EAF3FB]",
  grad: "bg-gradient-to-br from-[#EAF3FB] via-white to-[#D9ECFF]",
  badge: "bg-[#E6F0FA] text-[#0B5BD3]",
  border: "border-[#CBE2FF]",
};

// Product theming: gradient only
function productStyle(primaryTag: string = "AI") {
  switch (primaryTag) {
    case "ReferralCX":
      return { grad: "from-[#0054A6]/20 via-[#7AC043]/10 to-white" };
    case "CellCX":
      return { grad: "from-[#C1E1C5] via-white to-[#A7D7B5]" };
    case "ProbationCX":
      return { grad: "from-[#FDE68A] via-white to-[#FCD34D]" };
    case "VaxManagerCX":
      return { grad: "from-[#BAE6FD] via-white to-[#7DD3FC]" };
    default: // AI
      return { grad: "from-[#D0E4FF] via-white to-[#BFD7FF]" };
  }
}

function Hero({ onSearch }: { onSearch?: (q: string) => void }) {
  return (
    <div className={`${liteBlue.grad} border-b`}>
      <div className="mx-auto max-w-6xl px-4 py-12">
        <p className="text-xs uppercase tracking-widest text-[#0B5BD3]">Insights & Perspectives</p>
        <h1 className="mt-2 text-3xl font-bold leading-tight md:text-4xl">Blog — Digital Operations for Healthcare, Justice & Public Health</h1>
        <p className="mt-3 max-w-2xl text-slate-700">Practical guides, case stories, and product thinking from the EceptionistCX team. No fluff—just what works in the real world.</p>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
            <Input onChange={(e)=> onSearch?.(e.target.value)} placeholder="Search articles…" className="pl-9" />
          </div>
          <Button variant="outline" className="gap-2"><Filter className="h-4 w-4"/> Filters</Button>
        </div>
      </div>
    </div>
  );
}

function Filters({ categories, active, setActive, sort, setSort }: { categories: string[]; active: string; setActive: (v: string)=>void; sort: string; setSort: (v: string)=>void; }) {
  return (
    <div className="mx-auto max-w-6xl px-4">
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <Badge
              key={c}
              onClick={() => setActive(c === active ? "All" : c)}
              className={`cursor-pointer px-3 py-1 ${active === c ? "bg-[#0B5BD3] text-white" : liteBlue.badge} transition-colors`}
            >
              {c}
            </Badge>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Sort by</span>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className={`w-40 border ${liteBlue.border} bg-white`}>
              <SelectValue placeholder="Latest" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="latest">Latest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
              <SelectItem value="read">Read time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function CardArt({ tag }: { tag: string }) {
  const { grad } = productStyle(tag);
  return (
    <div className={`relative h-40 w-full bg-gradient-to-br ${grad}`}>
      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, #0B5BD3 2px, transparent 2px), radial-gradient(circle at 80% 70%, #7AC043 2px, transparent 2px)",
          backgroundSize: "60px 60px",
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white/80 text-2xl font-bold text-[#0B5BD3] shadow">
          CX
        </div>
      </div>
    </div>
  );
}

function BlogCard({ post, onOpen }: { post: any; onOpen: (p: any)=>void }) {
  const primary = post.tags[0] || "AI";
  return (
    <Card className={`group overflow-hidden rounded-2xl ${liteBlue.border} border hover:shadow-md transition-shadow h-full flex flex-col`}>
      <CardHeader className="p-0">
        <CardArt tag={primary} />
      </CardHeader>
      <CardContent className="space-y-2 p-4">
        <p className="text-xs font-medium uppercase tracking-widest text-[#0B5BD3]">{post.kicker}</p>
        <h3 className="text-lg font-semibold leading-snug">{post.title}</h3>
        <p className="line-clamp-2 text-sm text-slate-600">{post.excerpt}</p>
        <div className="flex flex-wrap gap-2 pt-1">
          {post.tags.map((t: string) => (
            <Badge key={t} variant="secondary" className={`${liteBlue.badge} text-xs`}>{t}</Badge>
          ))}
        </div>
      </CardContent>
      <CardFooter className="mt-auto w-full p-4 text-xs text-slate-500">
        <div className="flex flex-col gap-1 w-full">
          <div className="flex items-center gap-2"><User className="h-3.5 w-3.5"/><span>{post.author}</span></div>
          <div className="flex items-center justify-between gap-2 w-full">
            <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5"/><span>{formatDate(post.date)}</span></div>
            <Button size="sm" className="gap-1 bg-black text-white hover:bg-black/90" onClick={() => onOpen(post)}>
              Read <ChevronRight className="h-4 w-4"/>
            </Button>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
}

function Sidebar() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubscribe = async () => {
    if (!email.trim()) {
      setErrorMessage('Please enter your email address');
      setSubscriptionStatus('error');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
      setSubscriptionStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubscriptionStatus('idle');
    setErrorMessage('');

    try {
      const result = await emailService.sendNewsletterSignup(email);
      
      if (result.success) {
        setSubscriptionStatus('success');
        setEmail(''); // Clear the input on success
      } else {
        setSubscriptionStatus('error');
        setErrorMessage(result.error || 'Failed to subscribe. Please try again.');
      }
    } catch (error) {
      setSubscriptionStatus('error');
      setErrorMessage('An unexpected error occurred. Please try again.');
      console.error('Newsletter subscription error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubscribe();
    }
  };

  return (
    <div className="sticky top-20 space-y-6">
      <Card className={`rounded-2xl ${liteBlue.border} border`}>
        <CardContent className="space-y-3 p-4">
          <p className="text-sm font-semibold">Get new posts via email</p>
          
          {subscriptionStatus === 'success' ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <CheckCircle className="h-4 w-4" />
                <span>Successfully subscribed!</span>
              </div>
              <p className="text-xs text-slate-600">
                You'll receive our latest blog posts and updates via email.
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={() => setSubscriptionStatus('idle')}
              >
                Subscribe Another Email
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Input 
                placeholder="Your email" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isSubmitting}
                className={subscriptionStatus === 'error' ? 'border-red-500' : ''}
              />
              
              {subscriptionStatus === 'error' && (
                <div className="flex items-center gap-2 text-red-600 text-xs">
                  <AlertCircle className="h-3 w-3" />
                  <span>{errorMessage}</span>
                </div>
              )}
              
              <Button 
                className="w-full bg-black text-white hover:bg-black/90" 
                onClick={handleSubscribe}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// Note: FeaturedHero is currently unused because the listing shows all cards.

function DetailPage({ post, onBack, onOpenPost }: { post: any; onBack: ()=>void; onOpenPost: (p: any)=>void }) {
  const navigation = useNavigationStore();
  const primary = post.tags[0] || "AI";
  const { grad } = productStyle(primary);
  const cta = useMemo(() => {
    switch (primary) {
      case "ReferralCX":
        return {
          title: "See ReferralCX in action",
          body: "Unify internal and external referrals with capacity‑aware scheduling and guided triage.",
        };
      case "AI":
      case "AI in CX":
        return {
          title: "See AI in CX in action",
          body: "AI‑powered referral capture, triage, and capacity management—embedded directly into your workflows.",
        };
      case "CellCX":
        return {
          title: "See CellCX in action",
          body: "Purpose‑built workflows for cell therapy—milestones and cross‑team orchestration.",
        };
      case "ProbationCX":
        return {
          title: "See ProbationCX in action",
          body: "Digitize referrals, automate case workflows, and improve compliance with real‑time dashboards.",
        };
      case "VaxManagerCX":
        return {
          title: "See VaxManagerCX in action",
          body: "Scalable vaccine and clinic management—scheduling, wait lists, forms, and reporting in one platform.",
        };
      default:
        return {
          title: "Ready to modernize your operations?",
          body: "See how EceptionistCX unifies referrals, scheduling, and complex workflows.",
        };
    }
  }, [primary]);
  const relatedPosts = useMemo(() => {
    const currentTags: string[] = post.tags || [];
    const candidates = BLOG_POSTS.filter(p => p.id !== post.id);
    const scored = candidates.map(p => {
      const overlap = (p.tags || []).filter((t: string) => currentTags.includes(t)).length;
      return { p, score: overlap };
    });
    const sorted = scored
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score; // more tag overlap first
        return new Date(b.p.date as any as string).getTime() - new Date(a.p.date as any as string).getTime(); // newer first
      })
      .map(s => s.p)
      .slice(0, 3);
    return sorted;
  }, [post]);
  return (
    <div className="w-full">
      <div className="mx-auto max-w-3xl px-4 py-6">
        <Button variant="ghost" className="-ml-2 mb-4 gap-2" onClick={onBack}><ArrowLeft className="h-4 w-4"/> Back to Blog</Button>
        <div className={`rounded-2xl border bg-white ${liteBlue.border}`}>
          <div className={`relative h-56 w-full rounded-t-2xl bg-gradient-to-br ${grad}`}>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-white/80 text-2xl font-bold text-[#0B5BD3] shadow">
                CX
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5"/>{formatDate(post.date)}</span>
              <span>•</span>
              <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5"/>{post.author}</span>
              <span>•</span>
              <span className="inline-flex items-center gap-1"><Tag className="h-3.5 w-3.5"/>{post.tags.join(", ")}</span>
              <Button variant="ghost" size="sm" className="ml-auto gap-2"><Share2 className="h-4 w-4"/>Share</Button>
            </div>
            <h1 className="mt-3 text-3xl font-bold leading-tight">{post.title}</h1>
            {post.subtitle && <p className="mt-2 text-slate-600">{post.subtitle}</p>}

            {post.sections && (
              <div className={`mt-6 rounded-xl border p-4 ${liteBlue.border} ${liteBlue.bgBand}`}>
                <div className="text-xs font-semibold uppercase tracking-widest text-[#0B5BD3]">On this page</div>
                <ol className="mt-2 list-decimal pl-5 text-sm text-slate-700 space-y-1">
                  {post.sections.map((sec: any, i: number) => (
                    <li key={i}><a href={`#sec-${i}`} className="hover:underline">{sec.h2}</a></li>
                  ))}
                </ol>
              </div>
            )}

            <Separator className="my-8" />
            <div className="prose prose-slate max-w-none">
              {post.sections
                ? post.sections.map((sec: any, i: number) => (
                    <section id={`sec-${i}`} key={i} className="scroll-mt-28">
                      <h2 className="mt-10 text-2xl font-semibold">{sec.h2}</h2>
                      {sec.p?.map((para: string, j: number) => (
                        <p key={j} dangerouslySetInnerHTML={{ __html: para.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                      ))}
                      {sec.bullets && (
                        <ul className="list-disc pl-5">
                          {sec.bullets.map((b: string, k: number) => (
                            <li key={k} dangerouslySetInnerHTML={{ __html: b }} />
                          ))}
                        </ul>
                      )}
                      {sec.checklist && (
                        <div className={`mt-4 rounded-xl border p-4 ${liteBlue.border} ${liteBlue.bgBand}`}>
                          <div className="text-sm font-semibold">Implementation checklist</div>
                          <ul className="mt-2 list-disc pl-5">
                            {sec.checklist.map((b: string, k: number) => (
                              <li key={k}>{b}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {sec.note && (
                        <p className="mt-2 text-xs text-slate-500">{sec.note}</p>
                      )}
                    </section>
                  ))
                : post.body?.split("\n").map((para: string, i: number) => (
                    <p
                      key={i}
                      dangerouslySetInnerHTML={{
                        __html: para
                          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          .replace(/\- (.*?)(\n|$)/g, '• $1'),
                      }}
                    />
                  ))}
            </div>

            {/* In-card CTA under Implementation checklist, matching prototype style */}
            <div className={`my-8 border-t ${liteBlue.border}`} />
            <div className={`rounded-2xl border p-6 ${liteBlue.border} ${liteBlue.grad}`}>
              <div className="grid items-start gap-4 md:grid-cols-[1.5fr_.5fr]">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{cta.title}</div>
                  <p className="text-slate-700 break-words">{cta.body}</p>
                </div>
                <div className="flex gap-3 md:justify-end">
                  <Button className="bg-black text-white hover:bg-black/90" onClick={() => navigation.openDemoModal()}>Schedule a Demo</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-10">
          <div className="mb-3 text-sm font-semibold">Related posts</div>
          <div className="grid gap-4 md:grid-cols-3">
            {relatedPosts.map((rp) => (
              <Card
                key={rp.id}
                className={`overflow-hidden rounded-2xl ${liteBlue.border} border cursor-pointer hover:shadow-md transition-shadow`}
                onClick={() => onOpenPost(rp)}
              >
                <div className={`relative h-24 ${liteBlue.grad}`}>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/80 text-lg font-bold text-[#0B5BD3] shadow">
                      CX
                    </div>
                  </div>
                </div>
                <CardContent className="p-4">
                  <div className="text-sm font-semibold leading-snug line-clamp-2">{rp.title}</div>
                  <div className="mt-2 text-xs text-slate-500">Read <ChevronRight className="inline h-3.5 w-3.5"/></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ListingPage({ openPost }: { openPost: (p: any)=>void }) {
  const navigation = useNavigationStore();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState("All");
  const [sort, setSort] = useState("latest");

  const categories = ["All", "ReferralCX", "ProbationCX", "CellCX", "VaxManagerCX", "AI"];

  const posts = useMemo(() => {
    let arr = POSTS.filter(
      (p) => (active === "All" || p.tags.includes(active)) &&
        (query === "" || (p.title + p.excerpt + p.tags.join(" ")).toLowerCase().includes(query.toLowerCase()))
    );
    if (sort === "latest") arr = arr.sort((a, b) => new Date(b.date as any as string).getTime() - new Date(a.date as any as string).getTime());
    if (sort === "oldest") arr = arr.sort((a, b) => new Date(a.date as any as string).getTime() - new Date(b.date as any as string).getTime());
    if (sort === "read") arr = arr.sort((a, b) => (a.read || 0) - (b.read || 0));
    return arr;
  }, [query, active, sort]);

  return (
    <>
      <Hero onSearch={setQuery} />
      {/* Showing all cards in the grid; no featured hero */}

      <div className={`${liteBlue.bgBand} py-4`}>
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-sm font-semibold text-slate-700">Latest articles</div>
        </div>
        <Filters categories={categories} active={active} setActive={setActive} sort={sort} setSort={setSort} />
      </div>

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 pb-16 pt-6 md:grid-cols-[1fr_320px]">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {posts.map((p) => (
            <BlogCard key={p.id} post={p} onOpen={openPost} />
          ))}
        </div>
        <aside className="hidden md:block">
          <Sidebar />
        </aside>
        </div>
        
      <div className={`border-t ${liteBlue.grad}`}>
        <div className="mx-auto max-w-6xl px-4 py-12">
          <div className="grid items-center gap-4 md:grid-cols-[1.5fr_.5fr]">
            <div>
              <h3 className="text-xl font-semibold">Ready to modernize your operations?</h3>
              <p className="text-slate-700">See how EceptionistCX unifies referrals, scheduling, and complex workflows.</p>
            </div>
            <div className="flex gap-3 md:justify-end">
              <Button className="bg-black text-white hover:bg-black/90" onClick={() => navigation.openDemoModal()}>Schedule a Demo</Button>
              <Button variant="outline" onClick={() => navigate('/whitepapers')}>Explore White Papers</Button>
            </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default function Blog() {
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [activePost, setActivePost] = useState<any | null>(null);
  const location = useLocation();

  // If the user clicks the global Blog menu (navigates to /blog), ensure we return to the listing
  useEffect(() => {
    if (location.pathname === "/blog") {
      setIsDetailOpen(false);
      setActivePost(null);
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, [location.key]);

  const openPost = (post: any) => {
    setActivePost(post);
    setIsDetailOpen(true);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const goHome = () => {
    setIsDetailOpen(false);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };


  return (
    <div className="min-h-screen w-full bg-white text-slate-900">
      {!isDetailOpen ? (
        <div className="mx-auto max-w-6xl px-4 pt-6">
          <ListingPage openPost={(post) => openPost(post)} />
        </div>
      ) : (
        activePost && <DetailPage post={activePost} onBack={goHome} onOpenPost={openPost} />
      )}

    </div>
  );
}


